import { useState } from 'react';
import {
  FormErrorState,
  FormValidationState,
  DesignationOption
} from '../types/candidate.types';
import {
  validateName,
  validateNIC,
  validateEmail,
  validateMobileNumber,
  validateDate,
  validatePhoneNumberWithCountry
} from '../utills/validation.utils';

interface UseCandidateValidationProps {
  firstPageValidation: FormValidationState;
  secondPageValidation: FormValidationState;
  thirdPageValidation: FormValidationState;
  selectedDesignation: DesignationOption | null;
  selectedManager: any;
  selectedUserRole: any;
  selectedGender: string;
  selectedMaritalStatus: string;
  selectedType: string;
  setFirstErrors: React.Dispatch<React.SetStateAction<FormErrorState>>;
  setSecondErrors: React.Dispatch<React.SetStateAction<FormErrorState>>;
  setThirdErrors: React.Dispatch<React.SetStateAction<FormErrorState>>;
  isCreateEmsUserMode?: boolean;
}

export const useCandidateValidation = ({
  firstPageValidation,
  secondPageValidation,
  thirdPageValidation,
  selectedDesignation,
  selectedManager,
  selectedUserRole,
  selectedGender,
  selectedMaritalStatus,
  selectedType,
  setFirstErrors,
  setSecondErrors,
  setThirdErrors,
  isCreateEmsUserMode = false
}: UseCandidateValidationProps) => {
  const validateAndSetErrors = (): boolean => {
    let hasErrors = false;

    const newFirstErrors: FormErrorState = {};
    const newSecondErrors: FormErrorState = {};
    const newThirdErrors: FormErrorState = {};

    // First Name validation (always required)
    const firstNameValidation = validateName(firstPageValidation.firstName);
    if (!firstNameValidation.isValid) {
      newFirstErrors.firstNameError = firstNameValidation.errorMessage;
      hasErrors = true;
    }

    // Last Name validation (always required)
    const lastNameValidation = validateName(firstPageValidation.lastName);
    if (!lastNameValidation.isValid) {
      newFirstErrors.lastNameError = lastNameValidation.errorMessage;
      hasErrors = true;
    }

    // NIC validation - required in createEmsUserMode
    if (isCreateEmsUserMode) {
      if (!firstPageValidation.nic || firstPageValidation.nic.trim() === '') {
        newFirstErrors.nicError = 'NIC is required';
        hasErrors = true;
      } else if (!validateNIC(firstPageValidation.nic)) {
        newFirstErrors.nicError = 'Invalid NIC format (Format: 793699239V or 000099003556)';
        hasErrors = true;
      }
    } else {
      // Optional validation for non-createEmsUserMode
      if (
        firstPageValidation.nic &&
        firstPageValidation.nic.trim() !== '' &&
        !validateNIC(firstPageValidation.nic)
      ) {
        newFirstErrors.nicError = 'Invalid NIC format (Format: 793699239V or 000099003556)';
        hasErrors = true;
      }
    }

    // Date of Birth validation - required in createEmsUserMode
    if (isCreateEmsUserMode) {
      if (!firstPageValidation.dateOfBirth || firstPageValidation.dateOfBirth.trim() === '') {
        newFirstErrors.dateOfBirthError = 'Date of Birth is required';
        hasErrors = true;
      } else if (!validateDate(firstPageValidation.dateOfBirth)) {
        newFirstErrors.dateOfBirthError = 'Invalid Date Format';
        hasErrors = true;
      }
    } else {
      // Optional validation for non-createEmsUserMode
      if (
        firstPageValidation.dateOfBirth &&
        firstPageValidation.dateOfBirth.trim() !== '' &&
        !validateDate(firstPageValidation.dateOfBirth)
      ) {
        newFirstErrors.dateOfBirthError = 'Invalid Date Format';
        hasErrors = true;
      }
    }

    // Gender validation - required in createEmsUserMode
    if (isCreateEmsUserMode && (!selectedGender || selectedGender.trim() === '')) {
      newFirstErrors.genderError = 'Gender is required';
      hasErrors = true;
    }

    // Marital Status validation - required in createEmsUserMode
    if (isCreateEmsUserMode && (!selectedMaritalStatus || selectedMaritalStatus.trim() === '')) {
      newFirstErrors.maritalStatusError = 'Marital Status is required';
      hasErrors = true;
    }

    // Personal Email validation - required in createEmsUserMode
    if (isCreateEmsUserMode) {
      if (!firstPageValidation.personalEmail || firstPageValidation.personalEmail.trim() === '') {
        newFirstErrors.personalEmailError = 'Personal Email is required';
        hasErrors = true;
      } else if (!validateEmail(firstPageValidation.personalEmail)) {
        newFirstErrors.personalEmailError = 'Invalid Email Format (must contain @ and end with .com)';
        hasErrors = true;
      }
    } else {
      // Optional validation for non-createEmsUserMode
      if (
        firstPageValidation.personalEmail &&
        firstPageValidation.personalEmail.trim() !== '' &&
        !validateEmail(firstPageValidation.personalEmail)
      ) {
        newFirstErrors.personalEmailError = 'Invalid Email Format (must contain @ and end with .com)';
        hasErrors = true;
      }
    }

    // Mobile Number validation (always required)
    const mobileNumberValidation = validatePhoneNumberWithCountry(
      secondPageValidation.mobileNumber || '',
      secondPageValidation.selectedCountry
    );

    if (!mobileNumberValidation.isValid) {
      newSecondErrors.mobileNumberError = mobileNumberValidation.errorMessage;
      hasErrors = true;
    }

    // Emergency Contact validation - required in createEmsUserMode
    if (isCreateEmsUserMode) {
      if (!secondPageValidation.emergencyContactNumber || secondPageValidation.emergencyContactNumber.trim() === '') {
        newSecondErrors.emergencyContactNumberError = 'Emergency Contact Number is required';
        hasErrors = true;
      } else {
        const emergencyContactValidation = validatePhoneNumberWithCountry(
          secondPageValidation.emergencyContactNumber,
          secondPageValidation.emergencyContactCountry || 'lk'
        );

        if (!emergencyContactValidation.isValid) {
          newSecondErrors.emergencyContactNumberError = emergencyContactValidation.errorMessage;
          hasErrors = true;
        }
      }

      if (!secondPageValidation.emergencyContactName || secondPageValidation.emergencyContactName.trim() === '') {
        newSecondErrors.emergencyContactNameError = 'Emergency Contact Name is required';
        hasErrors = true;
      } else if (secondPageValidation.emergencyContactName.length > 50) {
        newSecondErrors.emergencyContactNameError = 'Character limit exceeded (Maximum 50 characters)';
        hasErrors = true;
      }

      if (!secondPageValidation.emergencyContactRelationship || secondPageValidation.emergencyContactRelationship.trim() === '') {
        newSecondErrors.emergencyContactRelationshipError = 'Emergency Contact Relationship is required';
        hasErrors = true;
      } else if (secondPageValidation.emergencyContactRelationship.length > 30) {
        newSecondErrors.emergencyContactRelationshipError = 'Character limit exceeded (Maximum 30 characters)';
        hasErrors = true;
      }
    } else {
      // Optional validation for non-createEmsUserMode
      if (
        secondPageValidation.emergencyContactNumber &&
        secondPageValidation.emergencyContactNumber.trim() !== ''
      ) {
        const emergencyContactValidation = validatePhoneNumberWithCountry(
          secondPageValidation.emergencyContactNumber,
          secondPageValidation.emergencyContactCountry || 'lk'
        );

        if (!emergencyContactValidation.isValid) {
          newSecondErrors.emergencyContactNumberError = emergencyContactValidation.errorMessage;
          hasErrors = true;
        }
      }

      if (
        secondPageValidation.emergencyContactName &&
        secondPageValidation.emergencyContactName.length > 50
      ) {
        newSecondErrors.emergencyContactNameError = 'Character limit exceeded (Maximum 50 characters)';
        hasErrors = true;
      }

      if (
        secondPageValidation.emergencyContactRelationship &&
        secondPageValidation.emergencyContactRelationship.length > 30
      ) {
        newSecondErrors.emergencyContactRelationshipError = 'Character limit exceeded (Maximum 30 characters)';
        hasErrors = true;
      }
    }

    // Address validations - required in createEmsUserMode
    if (isCreateEmsUserMode) {
      if (!secondPageValidation.permanentAddress || secondPageValidation.permanentAddress.trim() === '') {
        newSecondErrors.permanentAddressError = 'Permanent Address is required';
        hasErrors = true;
      } else if (secondPageValidation.permanentAddress.length > 300) {
        newSecondErrors.permanentAddressError = 'Character limit exceeded (Maximum 300 characters)';
        hasErrors = true;
      }

      if (!secondPageValidation.currentAddress || secondPageValidation.currentAddress.trim() === '') {
        newSecondErrors.currentAddressError = 'Current Address is required';
        hasErrors = true;
      } else if (secondPageValidation.currentAddress.length > 300) {
        newSecondErrors.currentAddressError = 'Character limit exceeded (Maximum 300 characters)';
        hasErrors = true;
      }
    } else {
      // Optional validation for non-createEmsUserMode
      if (
        secondPageValidation.permanentAddress &&
        secondPageValidation.permanentAddress.length > 300
      ) {
        newSecondErrors.permanentAddressError = 'Character limit exceeded (Maximum 300 characters)';
        hasErrors = true;
      }

      if (
        secondPageValidation.currentAddress &&
        secondPageValidation.currentAddress.length > 300
      ) {
        newSecondErrors.currentAddressError = 'Character limit exceeded (Maximum 300 characters)';
        hasErrors = true;
      }
    }

    // Employment validations - all required in createEmsUserMode
    if (isCreateEmsUserMode) {
      if (!thirdPageValidation.employeeId || thirdPageValidation.employeeId.trim() === '') {
        newThirdErrors.employeeIdError = 'Employee ID is required';
        hasErrors = true;
      }

      if (!selectedType || selectedType.trim() === '' || !thirdPageValidation.type || thirdPageValidation.type.trim() === '') {
    newThirdErrors.employmentTypeError = 'Employment Type is required';
    hasErrors = true;
  }

      if (!selectedManager) {
        newThirdErrors.reportingManagerError = 'Primary Reporting Manager is required';
        hasErrors = true;
      }

      if (!selectedUserRole) {
        newThirdErrors.userRoleError = 'User Role is required';
        hasErrors = true;
      }

      if (!thirdPageValidation.fromDate || thirdPageValidation.fromDate.trim() === '') {
        newThirdErrors.fromDateError = 'From Date is required';
        hasErrors = true;
      } else if (!validateDate(thirdPageValidation.fromDate)) {
        newThirdErrors.fromDateError = 'Invalid Date Format';
        hasErrors = true;
      }
    } else {
      // Standard validation for non-createEmsUserMode
      if (
        thirdPageValidation.fromDate &&
        thirdPageValidation.fromDate.trim() !== '' &&
        !validateDate(thirdPageValidation.fromDate)
      ) {
        newThirdErrors.fromDateError = 'Invalid Date Format';
        hasErrors = true;
      }
    }

    // Standard employment validations (always required)
    if (!selectedDesignation) {
      newThirdErrors.designationError = 'Designation is Required';
      hasErrors = true;
    }

    if (
      !thirdPageValidation.joinDate ||
      thirdPageValidation.joinDate.trim() === ''
    ) {
      newThirdErrors.joinDateError = 'Join Date is Required';
      hasErrors = true;
    } else if (!validateDate(thirdPageValidation.joinDate)) {
      newThirdErrors.joinDateError = 'Invalid Date Format';
      hasErrors = true;
    }

    setFirstErrors(newFirstErrors);
    setSecondErrors(newSecondErrors);
    setThirdErrors(newThirdErrors);

    return !hasErrors;
  };

  const clearError = (
    section: 'first' | 'second' | 'third',
    errorKey: string
  ) => {
    switch (section) {
      case 'first':
        setFirstErrors((prev) => ({ ...prev, [errorKey]: '' }));
        break;
      case 'second':
        setSecondErrors((prev) => ({ ...prev, [errorKey]: '' }));
        break;
      case 'third':
        setThirdErrors((prev) => ({ ...prev, [errorKey]: '' }));
        break;
    }
  };

  return {
    validateAndSetErrors,
    clearError
  };
};